import { Api } from "@/api"
import Icon from "@/components/Icon"
import BootstrapButton from "@/components/form/BootstrapButton"
import { showErrorMessage } from "@/composition/useLoading"
import { SystemRole, type DocumentUpload } from "@/models"
import { useLoggedInUser } from "@/stores/loggedInUserStore"
import usePopups from "@/stores/popupsStore"
import { getFileIcon } from "@/utils/fileIcons"
import { faCalendar } from "@fortawesome/free-regular-svg-icons"
import { faDownload, faEye, faTrashAlt, faUser } from "@fortawesome/free-solid-svg-icons"
import { css } from "vite-css-in-js"
import { ref, toRef } from "vue"
import { defineComponent, formatFileSize, inferProps, proxyProp, type ReactiveComponent } from "vue-utils"

interface Props {
	document: DocumentUpload
	delete(): void
}

const rowStyles = css`
	td {
		padding: 0.35rem 0.5rem;
		border-bottom: 1px solid rgba(0, 0, 0, 0.25);

		display: flex;
		column-gap: 0.25rem;
		align-items: center;

		font-size: 1rem;

		svg {
			color: #333;
		}
	}
	td:first-child {
		padding-left: 0.25rem;
		column-gap: 0.5rem;
		svg {
			font-size: 1.25rem;
		}
		span {
			white-space: nowrap;
			text-overflow: ellipsis;
			overflow: hidden;
		}
	}
	td:nth-child(3) svg {
		color: #0e406e;
	}
	td:nth-child(4) {
		text-align: right;
		justify-content: end;
	}
	td:last-child {
		padding-right: 0;
		svg {
			color: white;
		}
	}
`

const ExistingDocumentRow: ReactiveComponent<Props> = (props) => {
	const user = useLoggedInUser()
	const popups = usePopups()

	const document = proxyProp(props, "document")
	const uploadedBy = toRef(() => document.uploadedBy.user)

	const downloading = ref(false)
	const previewing = ref(false)

	async function download(preview: boolean) {
		const downloadRef = preview ? previewing : downloading
		if (downloadRef.value) {
			return
		}
		downloadRef.value = true
		try {
			let downloadLink = await Api.downloads.generateLink(props.document.guid)
			if (preview) {
				downloadLink += "?preview=true"
			}
			window.open(downloadLink, "_blank", "noopener noreferrer")
		} catch (e) {
			void showErrorMessage("Failed to download file", (e as Error).message)
		} finally {
			downloadRef.value = false
		}
	}

	async function handleDeleteClick() {
		const confirmation = await popups.confirmDelete({
			title: "Confirm Delete",
			content: () => (
				<>Are you sure you wish to delete this document upload? All access history information will also be deleted.</>
			),
		})
		if (confirmation) {
			props.delete()
		}
	}

	return () => (
		<tr key={document.guid} class={rowStyles}>
			<td>
				<Icon icon={getFileIcon(document.mimeType)} />
				<span title={document.fileName}>{document.fileName}</span>
			</td>
			<td title="Date Uploaded">
				<Icon icon={faCalendar} />
				<span title={document.uploadedBy.timeStamp.toGBDateTimeString()}>
					{document.uploadedBy.timeStamp.toGBDateString()}
				</span>
			</td>
			<td title="Uploaded By">
				<Icon icon={faUser} />
				<span>{uploadedBy.value ? `${uploadedBy.value.firstName} ${uploadedBy.value.lastName}` : "Unknown"}</span>
			</td>
			<td>{formatFileSize(document.fileSize)}</td>
			<td>
				<BootstrapButton
					color="primary"
					title="Preview File"
					isSubmitting={previewing.value}
					disabled={previewing.value}
					onClick={() => void download(true)}
				>
					{!previewing.value && <Icon icon={faEye} />}
				</BootstrapButton>
				<BootstrapButton
					color="success"
					title="Download File"
					isSubmitting={downloading.value}
					disabled={downloading.value}
					onClick={() => void download(false)}
				>
					{!downloading.value && <Icon icon={faDownload} />}
				</BootstrapButton>
				{user.atLeastHasRole(SystemRole.SuperAdmin) && (
					<BootstrapButton color="danger" title="Delete Upload" onClick={() => void handleDeleteClick()}>
						<Icon icon={faTrashAlt} />
					</BootstrapButton>
				)}
			</td>
		</tr>
	)
}

export default defineComponent(ExistingDocumentRow, inferProps<Props>())
